import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSync, faExclamationCircle, faStar } from '@fortawesome/free-solid-svg-icons';
import { schwabApi } from '../api/schwabApi';

function maskAccount(num) {
  if (!num) return '—';
  const s = String(num);
  return s.length <= 4 ? s : `••••${s.slice(-4)}`;
}

export default function SchwabPreferencesPanel() {
  const [pref, setPref] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await schwabApi.getUserPreference();
      setPref(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setPref(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !pref) {
    return (
      <div className="py-10 text-center">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-muted">
          Schwab user preferences — account nicknames, primary account, streamer metadata.
        </p>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center justify-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-hairline text-muted hover:text-ink self-start sm:self-auto"
        >
          <FontAwesomeIcon icon={faSync} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-hairline text-sm text-bear bg-elevated">
          <FontAwesomeIcon icon={faExclamationCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!pref ? (
        <div className="lt-card px-4 py-8 text-center text-sm text-muted">No preference data.</div>
      ) : (
        <>
          <div className="lt-card overflow-hidden">
            <div className="px-4 py-2.5 border-b border-hairline text-sm font-medium text-ink">
              Preference accounts ({pref.accounts?.length || 0})
            </div>
            <div className="md:hidden divide-y divide-hairline">
              {(pref.accounts || []).map((a) => (
                <div key={a.accountNumber} className="px-3 py-3 space-y-1">
                  <div className="font-medium text-ink flex items-center gap-1.5">
                    {a.primaryAccount && (
                      <FontAwesomeIcon icon={faStar} className="text-[10px] text-[var(--lt-warning)]" />
                    )}
                    {a.nickName || '—'}
                  </div>
                  <div className="text-[11px] text-muted">
                    {a.type || 'Account'} · {maskAccount(a.accountNumber)}
                    {a.displayAcctId ? ` · ${a.displayAcctId}` : ''}
                  </div>
                  <div className="text-[10px] text-muted">
                    {a.accountColor ? `Color: ${a.accountColor} · ` : ''}
                    {a.primaryAccount ? 'primary · ' : ''}
                    {a.autoPositionEffect ? 'auto PE' : 'manual PE'}
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted border-b border-hairline">
                    <th className="px-3 py-2">Nickname</th>
                    <th className="px-3 py-2">Display ID</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Account #</th>
                    <th className="px-3 py-2">Color</th>
                    <th className="px-3 py-2">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {(pref.accounts || []).map((a) => (
                    <tr
                      key={a.accountNumber}
                      className="border-b border-hairline last:border-0 hover:bg-elevated/50"
                    >
                      <td className="px-3 py-2 font-medium text-ink">
                        {a.primaryAccount && (
                          <FontAwesomeIcon icon={faStar} className="text-[10px] text-[var(--lt-warning)] mr-1.5" />
                        )}
                        {a.nickName || '—'}
                      </td>
                      <td className="px-3 py-2 num text-muted">{a.displayAcctId || '—'}</td>
                      <td className="px-3 py-2 text-xs">{a.type || '—'}</td>
                      <td className="px-3 py-2 num text-xs">{maskAccount(a.accountNumber)}</td>
                      <td className="px-3 py-2 text-xs">{a.accountColor || '—'}</td>
                      <td className="px-3 py-2 text-[10px] text-muted">
                        {a.primaryAccount ? 'primary · ' : ''}
                        {a.autoPositionEffect ? 'auto PE' : 'manual PE'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {(pref.offers || []).length > 0 && (
            <div className="lt-card px-4 py-3">
              <div className="text-sm font-medium text-ink mb-2">Offers / market data</div>
              <div className="space-y-1 text-xs text-muted">
                {pref.offers.map((o, i) => (
                  <div key={i}>
                    Level 2: {o.level2Permissions ? 'yes' : 'no'}
                    {o.mktDataPermission ? ` · ${o.mktDataPermission}` : ''}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(pref.streamerInfo || []).length > 0 && (
            <div className="lt-card px-4 py-3">
              <div className="text-sm font-medium text-ink mb-2">Streamer info</div>
              <div className="space-y-2 text-xs font-mono text-muted break-all">
                {pref.streamerInfo.map((s, i) => (
                  <div key={i} className="border-t border-hairline first:border-0 pt-2 first:pt-0">
                    <div>socket: {s.streamerSocketUrl || '—'}</div>
                    <div>customer: {s.schwabClientCustomerId || '—'}</div>
                    <div>correl: {s.schwabClientCorrelId || '—'}</div>
                    <div>
                      channel: {s.schwabClientChannel || '—'} · fn:{' '}
                      {s.schwabClientFunctionId || '—'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
